import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import OT from '@opentok/client';
import Logger from '@medflex/utils/Logger';
import SessionContext from './SessionContext';
import createStreamTrackingEventHandlers from './createStreamTrackingEventHandlers';

const connectionEquals = (connectionA, connectionB) => {
  if (connectionA.data && connectionB.data) {
    return connectionA.data === connectionB.data;
  }
  return false;
};

const Session = forwardRef(
  (
    {
      children,
      apiKey,
      id,
      token,
      turnservers,
      onCreated,
      onConnected,
      onDisconnected,
      onReconnecting,
      onReconnected,
      autoConnect,
      autoPublish,
      publisher,
      screenPublisher,
      isProxyInitialized,
      onStreamsChanged,
    },
    ref,
  ) => {
    const [connected, setConnected] = useState(false);
    const [isAlreadyConnected, setIsAlreadyConnected] = useState(false);
    const [streams, setStreams] = useState([]);
    const [session, setSession] = useState(null);

    const streamTrackingEventHandlers = useMemo(() => {
      const updateStreams = s => {
        setStreams(s);
        onStreamsChanged(s);
      };
      return createStreamTrackingEventHandlers({ onStreamsUpdated: updateStreams });
    }, []);

    useEffect(() => {
      if (apiKey && id && isProxyInitialized && turnservers) {
        const initialisedSession = OT.initSession(apiKey, id, {
          iceConfig: {
            includeServers: 'custom',
            transportPolicy: 'all',
            customServers: turnservers,
          },
        });

        if (initialisedSession) {
          onCreated({ session: initialisedSession });
          setSession(initialisedSession);
        }
      }
    }, [apiKey, id, isProxyInitialized, turnservers]);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (session && streamTrackingEventHandlers) {
        session.on(streamTrackingEventHandlers);

        return () => {
          session.off(streamTrackingEventHandlers);
        };
      }
    }, [session, streamTrackingEventHandlers]);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      const eventHandlers = {
        sessionReconnecting: () => onReconnecting(),
        sessionReconnected: () => onReconnected(),
      };

      if (session) {
        session.on(eventHandlers);
        return () => session.off(eventHandlers);
      }
    }, [session]);

    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(session);
          return;
        }
        /* eslint-disable-next-line */
        ref.current = session;
      }
    }, [session]);

    useEffect(() => {
      const handleConnected = () => {
        onConnected();
        setConnected(true);
      };

      const handleDisconnected = () => {
        setStreams([]);
        setConnected(false);
        onDisconnected();
      };

      const logOpentokEvent = event => {
        if (!event) return;

        if (event.reason) {
          Logger.log(`WebRTC Event - type '${event.type}', reason '${event.reason}'`);
        } else {
          Logger.log(`WebRTC Event - type '${event.type}'`);
        }
      };

      if (session) {
        session.on('sessionConnected', handleConnected);
        session.on('sessionDisconnected', handleDisconnected);
        session.on(
          'connectionCreated sessionConnected sessionReconnected sessionReconnecting streamCreated connectionDestroyed sessionDisconnected streamDestroyed',
          logOpentokEvent,
        );

        return () => {
          session.off('sessionConnected', handleConnected);
          session.off('sessionDisconnected', handleDisconnected);
          session.off(
            'connectionCreated sessionConnected sessionReconnected sessionReconnecting streamCreated connectionDestroyed sessionDisconnected streamDestroyed',
            logOpentokEvent,
          );
        };
      }
      return undefined;
    }, [session]);

    useEffect(() => {
      if (connected && autoPublish && publisher) {
        if (
          !session.streams.find(stream => connectionEquals(stream.connection, session.connection))
        ) {
          session.publish(publisher);
        }
      }
    }, [session, connected, publisher]);

    useEffect(() => {
      if (connected && autoPublish && screenPublisher) {
        session.publish(screenPublisher);
      }
    }, [session, connected, screenPublisher]);

    useEffect(() => {
      if (session && connected) {
        const alreadyConnectedStream = session.streams.find(stream =>
          connectionEquals(stream.connection, session.connection),
        );
        setIsAlreadyConnected(!!alreadyConnectedStream);
      }
    }, [session, connected]);

    useEffect(() => {
      if (session && token && !connected && autoConnect) {
        session.connect(token);
      }
    }, [session, connected, token, autoConnect]);

    const sessionContextValues = useMemo(
      () => ({
        session,
        connected,
        streams,
        publisher,
        isAlreadyConnected,
      }),
      [session, connected, streams.length, publisher, isAlreadyConnected],
    );

    return (
      <SessionContext.Provider value={sessionContextValues}>{children}</SessionContext.Provider>
    );
  },
);

Session.defaultProps = {
  onCreated: () => {},
  onConnected: () => {},
  onReconnecting: () => {},
  onReconnected: () => {},
  onDisconnected: () => {},
  onStreamsChanged: () => {},
  autoConnect: false,
  autoPublish: false,
  isProxyInitialized: false,
};

Session.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  publisher: PropTypes.any,
  screenPublisher: PropTypes.any,
  apiKey: PropTypes.string,
  id: PropTypes.string,
  token: PropTypes.string,
  turnservers: PropTypes.arrayOf([
    PropTypes.exact({
      urls: PropTypes.arrayOf([PropTypes.string]),
      username: PropTypes.string,
      credential: PropTypes.string,
    }),
  ]).isRequired,
  onCreated: PropTypes.func,
  onConnected: PropTypes.func,
  onReconnecting: PropTypes.func,
  onReconnected: PropTypes.func,
  onDisconnected: PropTypes.func,
  onStreamsChanged: PropTypes.func,
  autoConnect: PropTypes.bool,
  autoPublish: PropTypes.bool,
  isProxyInitialized: PropTypes.bool,
};

export default Session;
